type Consent = {
  functional: boolean;
  marketing: boolean;
};

type CopilotConfig = {
  [key: string]: unknown;
  user_is_logged_in?: boolean;
  is_copilot_enabled?: boolean | null;
  is_masquerade?: boolean;
  email?: string;
  created_at?: number | null;
  highest_account_mrr?: number | null;
  support_eligibility_plan_family?: string | null;
  hubspot_qualified_signup_score?: string | number | null;
  hubspot_job_seniority?: string | null;
  huspot_company_size?: string | null;
  hubspot_enriched_company_size?: string | null;
};

export type CopilotWindow = Window & {
  OnetrustActiveGroups?: string;
  OneTrust?: {
    OnConsentChanged?: (
      listener: (event: { detail?: string[] }) => void
    ) => void;
  };
  ZapierChat?: unknown;
  zapierCopilotWidget?: { config: CopilotConfig };
  zapierDeveloperCopilotStartup?: Promise<void>;
};

type Options = {
  apiBaseUrl?: string;
  appBaseUrl?: string;
};

const FUNCTIONAL_GROUP = 'C0003';
const MARKETING_GROUP = 'C0004';
const POLL_INTERVAL = 250;
const MAX_CONSENT_ATTEMPTS = 100;
const WIDGET_SCRIPT_ID = '__copilotWidget';
const DEFAULT_CONSENT = { functional: false, marketing: false };
const QUALIFIED_JOB_SENIORITIES = ['Director', 'VP / Exec', 'VP/ Exec'];
const QUALIFIED_COMPANY_SIZES = ['250-999', '1,000+', '1,000-4,999', '5,000+'];
const ARR_BUCKETS: [number, number, string][] = [
  [0, 500, 'arr-0-500'],
  [500, 2000, 'arr-500-2000'],
  [2000, 10000, 'arr-2000-10000'],
  [10000, 20000, 'arr-10000-20000'],
  [20000, 50000, 'arr-20000-50000'],
  [50000, 100000, 'arr-50000-100000'],
  [100000, Infinity, 'arr-100000_plus'],
];

const host = window as CopilotWindow;

function readCookie(name: string): string | undefined {
  const cookie = document.cookie
    .split(';')
    .map(value => value.trim())
    .find(value => value.startsWith(`${name}=`));
  return cookie?.slice(name.length + 1);
}

function readConsent(): Consent | undefined {
  const groups = host.OnetrustActiveGroups;
  if (groups && groups !== ',,') {
    return {
      functional: groups.includes(FUNCTIONAL_GROUP),
      marketing: groups.includes(MARKETING_GROUP),
    };
  }

  const cookie = readCookie('OptanonConsent');
  if (cookie) {
    try {
      const groupValue = decodeURIComponent(cookie)
        .split('&')
        .find(value => value.startsWith('groups='));
      const enabledGroups = (groupValue?.slice('groups='.length) || '')
        .split(',')
        .filter(value => value.endsWith(':1'))
        .map(value => value.split(':')[0]);

      return {
        // Preserve the shared loader's cookie fallback: marketing consent also
        // counts as functional consent when the OneTrust SDK is unavailable.
        functional:
          enabledGroups.includes(FUNCTIONAL_GROUP) ||
          enabledGroups.includes(MARKETING_GROUP),
        marketing: enabledGroups.includes(MARKETING_GROUP),
      };
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function waitForConsent(): Promise<Consent> {
  const consent = readConsent();
  if (consent) return Promise.resolve(consent);

  return new Promise(resolve => {
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      const currentConsent = readConsent();
      if (currentConsent || attempts >= MAX_CONSENT_ATTEMPTS) {
        window.clearInterval(interval);
        resolve(currentConsent || DEFAULT_CONSENT);
      }
    }, POLL_INTERVAL);
  });
}

function listenForConsentChanges() {
  const register = () => {
    if (host.OneTrust?.OnConsentChanged) {
      host.OneTrust.OnConsentChanged(({ detail = [] }) => {
        const currentConfig = host.zapierCopilotWidget?.config;
        if (currentConfig) {
          const config = {
            ...currentConfig,
            functional_cookies_enabled: detail.includes(FUNCTIONAL_GROUP),
            marketing_consent: detail.includes(MARKETING_GROUP),
          };
          host.zapierCopilotWidget = { config };
        }
      });
      return true;
    }
    return false;
  };

  if (register()) return;

  let attempts = 0;
  const interval = window.setInterval(() => {
    attempts += 1;
    if (register() || attempts >= MAX_CONSENT_ATTEMPTS) {
      window.clearInterval(interval);
    }
  }, POLL_INTERVAL);
}

function readPageViewId(): string {
  try {
    const pageView = JSON.parse(sessionStorage.getItem('page_view_id') || '[]');
    return pageView?.[1] || '';
  } catch {
    return '';
  }
}

function getArrBucket(mrr: number | null | undefined): string {
  if (mrr === null || mrr === undefined) return 'arr-unknown';

  // The shared user API reports MRR in cents; buckets use annual dollars.
  const arr = Math.floor((mrr * 12) / 100);
  return (
    ARR_BUCKETS.find(([low, high]) => low <= arr && arr < high)?.[2] ||
    'arr-unknown'
  );
}

function isSupportToSalesSuppressed(user: CopilotConfig): boolean {
  const qualifiedSeniority = QUALIFIED_JOB_SENIORITIES.includes(
    user.hubspot_job_seniority || ''
  );
  // Preserve the shared loader's legacy `huspot_company_size` key.
  const qualifiedCompanySize =
    QUALIFIED_COMPANY_SIZES.includes(user.huspot_company_size || '') ||
    QUALIFIED_COMPANY_SIZES.includes(user.hubspot_enriched_company_size || '');
  if (qualifiedSeniority && qualifiedCompanySize) return false;

  const sevenDaysAgo = Math.floor(Date.now() / 1000) - 7 * 24 * 60 * 60;
  return (
    user.support_eligibility_plan_family === 'Free' &&
    (Number(user.created_at) < sevenDaysAgo ||
      !user.hubspot_qualified_signup_score)
  );
}

async function fetchUserConfig(apiBaseUrl: string): Promise<CopilotConfig> {
  const params = new URLSearchParams({
    bust_cache: 'false',
    sales_confirmation_page: 'false',
    sales_page: 'false',
    is_dev_platform_page: 'true',
  });
  const response = await fetch(
    `${apiBaseUrl.replace(/\/$/, '')}/api/in-product-messaging/user/?${params}`,
    { credentials: 'include' }
  );
  if (!response.ok) {
    throw new Error(`Copilot configuration request failed: ${response.status}`);
  }

  const user: CopilotConfig = await response.json();
  if (user === null || typeof user !== 'object' || Array.isArray(user)) {
    throw new Error('Invalid Copilot configuration response');
  }
  return user;
}

async function bootCopilot(apiBaseUrl: string, appBaseUrl: string) {
  const [user, consent] = await Promise.all([
    fetchUserConfig(apiBaseUrl),
    waitForConsent(),
  ]);
  if (
    user.user_is_logged_in !== true ||
    user.is_copilot_enabled === false ||
    user.is_masquerade ||
    user.email?.endsWith('@zapier.rodeo')
  ) {
    return;
  }

  const {
    intercom_app_id,
    intercom_user_hash,
    is_masquerade,
    ...userConfig
  } = user;
  const config = {
    user_has_signed_up: false,
    functional_cookies_enabled: consent.functional,
    marketing_consent: consent.marketing,
    page_view_id: readPageViewId(),
    pathname: window.location.pathname,
    url_address: window.location.href,
    session_id: readCookie('session_id'),
    visitor_id: readCookie('visitor_id'),
    disabled: false,
    support_to_sales_suppressed: isSupportToSalesSuppressed(user),
    // Match the shared loader: API values override defaults, then geo and ARR
    // are applied last. Developer pages do not perform a sales geo lookup.
    ...userConfig,
    country: null,
    region: null,
    continent: null,
    is_eu: null,
    highest_account_arr_bucket: getArrBucket(user.highest_account_mrr),
  };

  // Match the widget's existing configuration contract before loading its code.
  host.zapierCopilotWidget = { config };

  const cdnOrigin =
    new URL(appBaseUrl).hostname === 'zapier.com'
      ? 'https://cdn.zapier.com'
      : 'https://cdn.zapier-staging.com';
  const script = document.createElement('script');
  script.id = WIDGET_SCRIPT_ID;
  script.src = `${cdnOrigin}/packages/chat-widget/developer-platform-copilot/v1/index.js`;
  script.async = true;
  document.head.appendChild(script);

  listenForConsentChanges();
}

export function initializeDeveloperCopilot({
  apiBaseUrl = process.env.ZAPIER_API_BASE_URL || '',
  appBaseUrl = process.env.ZAPIER_APP_BASE_URL || window.location.origin,
}: Options = {}): Promise<void> {
  if (host.zapierDeveloperCopilotStartup) {
    return host.zapierDeveloperCopilotStartup;
  }
  if (
    host.ZapierChat ||
    host.zapierCopilotWidget ||
    document.getElementById(WIDGET_SCRIPT_ID)
  ) {
    return Promise.resolve();
  }

  // A window-level promise also guards concurrent execution of the entry bundle.
  const startup = bootCopilot(apiBaseUrl, appBaseUrl).catch(error => {
    delete host.zapierDeveloperCopilotStartup;
    console.error('Unable to start Developer Platform Copilot:', error);
  });
  host.zapierDeveloperCopilotStartup = startup;
  return startup;
}
